/**
 * useBusinessProfile Hook
 * Unified hook for both EntityProfile and MyBusinessProfile
 * Eliminates 300+ lines of duplication
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchBusinessById } from '../domain/useCases/Business/fetchBusinessById.js';
import { fetchBusinesses } from '../domain/useCases/Business/fetchBusinesses.js';
import { fetchTranslations } from '../domain/useCases/Translation/fetchTranslations.js';
import { fetchSocialLinks } from '../domain/useCases/SocialLink/fetchSocialLinks.js';
import { fetchAssets } from '../domain/useCases/Asset/fetchAssets.js';
import { fetchBanners } from '../domain/useCases/Banner/fetchBanners.js';
import { fetchBusinessRatings } from '../domain/useCases/Rating/fetchBusinessRatings.js';
import { switchProfile, getMyBusinessProfile } from '../data/repositories/authRepository.js';
import { useBusiness } from './useBusiness.js';
import { convertTo12Hour } from '../domain/utils/Time/convertTo12Hour.js';
import { isCurrentlyOpen } from '../domain/utils/Time/isCurrentlyOpen.js';
import { storageService } from '../domain/services/StorageService.js';
import { fetchWeather } from '../domain/utils/Weather/fetchWeather.js';
import { normalizeError, withRetry } from '../domain/utils/Error/normalizeError.js';
import html2canvas from 'html2canvas';

export const useBusinessProfile = (identifier, type = 'id', shouldSwitchProfile = false) => {
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showQRPlateModal, setShowQRPlateModal] = useState(false);

  const [socialLinks, setSocialLinks] = useState([]);
  const [relatedBusinesses, setRelatedBusinesses] = useState([]);
  const [translations, setTranslations] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('US');
  const [expandedDescription, setExpandedDescription] = useState(false);
  const [qrPlateData, setQrPlateData] = useState(null);
  const [assets, setAssets] = useState([]);
  const [banners, setBanners] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [weather, setWeather] = useState(null);
  const [counts, setCounts] = useState({
    views: 0,
    shares: 0,
    favorites: 0,
  });

  const qrCodeRef = useRef(null);
  const aboutSectionRef = useRef(null);
  const fetchedRef = useRef(false);
  const currentIdentifierRef = useRef(identifier);
  const abortControllerRef = useRef(null);

  const { getQRPlateData } = useBusiness();

  // Keyboard navigation for gallery
  useEffect(() => {
    if (!showGalleryModal || !business) return;

    const images = [
      business.coverImageUrl,
      business.avatar,
      business.buildingImageUrl,
      ...(business.gallery || []),
    ].filter(Boolean);

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
      } else if (e.key === 'Escape') {
        setShowGalleryModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showGalleryModal, business]);

  // Setup AbortController for cleanup
  useEffect(() => {
    abortControllerRef.current = new AbortController();
    
    return () => {
      // Abort all pending requests when component unmounts
      abortControllerRef.current?.abort();
    };
  }, []);

  // Main data fetch
  const fetchData = useCallback(async () => {
    if (fetchedRef.current && currentIdentifierRef.current === identifier) return;

    fetchedRef.current = true;
    currentIdentifierRef.current = identifier;

    try {
      setLoading(true);
      setError(null);

      // Fetch business data
      let businessData;
      if (type === 'tag') {
        console.log('Fetching business by tag:', identifier, 'with exact match');
        const result = await withRetry(() => fetchBusinesses({ tag: identifier, exact: true }));
        console.log('API result for tag search:', result);
        
        // Backend may return multiple results even with exact=true, so filter for exact match
        const businesses = result.data || result;
        businessData = businesses.find(b => b.tag === identifier);
        
        if (!businessData) throw new Error('Business not found');
        console.log('Found business:', businessData.name, 'with tag:', businessData.tag);
      } else {
        businessData = await withRetry(() => fetchBusinessById(identifier));
        if (!businessData) throw new Error('Business not found');
      }

      setBusiness(businessData);

      // Extract counts from API response
      const extractedCounts = {
        views: businessData?.views ?? 0,
        shares: businessData?.shares ?? 0,
        favorites: businessData?.favorites ?? 0,
      };
      setCounts(extractedCounts);

      // Call switch-profile API only if shouldSwitchProfile is true (MyBusinessProfile page)
      if (shouldSwitchProfile && businessData?.id) {
        try {
          const switchResponse = await switchProfile(businessData.id);
          
          // After switching profile, fetch counts from my-profile endpoint using the new access token
          if (switchResponse?.accessToken) {
            const myProfileData = await getMyBusinessProfile(switchResponse.accessToken);
            if (myProfileData) {
              const switchedCounts = {
                views: myProfileData?.businessViews?.views ?? myProfileData?.views ?? 0,
                shares: myProfileData?.businessViews?.share ?? myProfileData?.shares ?? 0,
                favorites: myProfileData?.businessViews?.favourites ?? myProfileData?.favorites ?? 0,
              };
              setCounts(switchedCounts);
            }
          }
        } catch (err) {
          console.warn('Switch profile error:', err);
          // Don't block page rendering if switch fails
        }
      }

      // Check if current user is the owner - ONLY if authenticated
      const userDataString = localStorage.getItem('userData');
      const hasAuthToken = storageService.hasAuthToken();
      
      if (hasAuthToken && userDataString && businessData.owner?.id) {
        try {
          const userData = JSON.parse(userDataString);
          setIsOwner(userData.id === businessData.owner.id);
        } catch (e) {
          setIsOwner(false);
        }
      } else {
        // No auth token or no userData - definitely not the owner
        setIsOwner(false);
      }

      // Fetch related data in parallel
      // NOTE: Multiple API calls are intentional - each provides different data
      // - language-translations: Get translations for business description
      // - social links: Get social media links
      // - assets: Get business assets
      // - banners: Get promotional banners
      // - ratings: Get business ratings
      // - qr-plates: Get QR code data
      // - weather: Get weather for business location
      // These are called in parallel for performance
      if (businessData.id) {
        Promise.allSettled([
          fetchTranslations('sampada', businessData.id).then(setTranslations).catch(err => {
            console.error('Translation fetch error:', err);
          }),
          fetchSocialLinks(businessData.id).then(data => setSocialLinks(Array.isArray(data) ? data : data.data || [])).catch(err => {
            console.error('Social links fetch error:', err);
          }),
          fetchAssets(businessData.id).then(setAssets).catch(err => {
            console.error('Assets fetch error:', err);
          }),
          fetchBanners(businessData.id).then(setBanners).catch(err => {
            console.error('Banners fetch error:', err);
          }),
          fetchBusinessRatings(businessData.id).then(data => setRatings(data.data || [])).catch(err => {
            console.error('Ratings fetch error:', err);
          }),
          getQRPlateData(businessData.id).then(qrDataArray => {
            if (Array.isArray(qrDataArray) && qrDataArray.length > 0) {
              setQrPlateData(qrDataArray[0]);
            }
          }).catch(err => {
            console.error('QR plate fetch error:', err);
          }),
          businessData.location?.coordinates ? fetchWeather(businessData.location.coordinates[0], businessData.location.coordinates[1]).then(setWeather).catch(err => {
            console.error('Weather fetch error:', err);
          }) : Promise.resolve(),
        ]);

        // Fetch related businesses
        const categoryIds = businessData.categoryIds
          || (businessData.category?.id ? [businessData.category.id] : null)
          || (Array.isArray(businessData.categories) ? businessData.categories.map(c => c.id) : null);

        if (categoryIds?.length > 0 && businessData.location?.coordinates) {
          try {
            const geoPointsString = businessData.location.coordinates.join(',');
            const params = {
              page: '1',
              take: '10',
              distance: '80000',
              sortByDistance: 'ASC',
              geoPoints: geoPointsString,
              categoryIds: categoryIds,
            };
            const relatedResult = await fetchBusinesses(params);
            const relatedData = relatedResult.data || [];
            setRelatedBusinesses(relatedData.filter(b => b.id !== businessData.id && b.tag !== businessData.tag));
          } catch (err) {
            console.error('Related businesses fetch error:', err);
            setRelatedBusinesses([]);
          }
        }
      }
    } catch (err) {
      setError(normalizeError(err, 'business profile'));
    } finally {
      setLoading(false);
    }
  }, [identifier, type]);

  useEffect(() => {
    if (currentIdentifierRef.current !== identifier) {
      fetchedRef.current = false;
      setLoading(true);
      setError(null);
      setBusiness(null);
      setSocialLinks([]);
      setRelatedBusinesses([]);
      setTranslations(null);
      setAssets([]);
      setBanners([]);
      setRatings([]);
      setQrPlateData(null);
      setActiveImageIndex(0);
    }

    if (identifier) {
      fetchData();
    }
  }, [identifier, fetchData]);

  const getTranslatedDescription = useCallback(() => {
    if (!translations?.translationContent || translations.translationContent.length === 0) {
      return business?.description || '';
    }
    const translation = translations.translationContent.find(t => t.code === selectedLanguage);
    return translation?.content || business?.description || '';
  }, [translations, selectedLanguage, business]);

  const isLongContent = useCallback((content) => content && content.length > 300, []);

  const getTruncatedContent = useCallback((content) => {
    if (!content) return '';
    if (content.length <= 300) return content;
    return content.substring(0, 300).trim() + '...';
  }, []);

  const images = business ? [
    business.coverImageUrl,
    business.avatar,
    business.buildingImageUrl,
    ...(business.gallery || []),
  ].filter(Boolean) : [];

  const currentImage = images[activeImageIndex] || business?.coverImageUrl || business?.avatar;

  let workingHours = {};
  if (business?.workingDaysAndHours) {
    if (typeof business.workingDaysAndHours === 'string') {
      try {
        workingHours = JSON.parse(business.workingDaysAndHours);
      } catch (e) {
        workingHours = {};
      }
    } else {
      workingHours = business.workingDaysAndHours;
    }
  }

  const handleShare = useCallback(() => {
    navigator.share?.({
      title: business?.name,
      url: window.location.href,
    });
  }, [business]);

  const handleImageClick = useCallback((imageUrl) => {
    setPreviewImage(imageUrl);
    setShowImagePreview(true);
  }, []);

  const handleBack = useCallback(() => {
    // Only redirect to dashboard if user is fully authenticated (has both token AND user data)
    if (storageService.isAuthenticated()) {
      navigate('/dashboard?tab=my-businesses');
    } else {
      navigate('/map');
    }
  }, [navigate]);

  const downloadQRCode = useCallback(async (format = 'png') => {
    const qrElement = document.querySelector('#qrCodeContainer');
    if (!qrElement) {
      alert('QR Code not found');
      return;
    }

    try {
      const originalClass = qrElement.className;
      const scaleRegex = /(scale-\d+|sm:scale-\d+|md:scale-\d+|lg:scale-\d+)/g;
      const cleanClass = originalClass.replace(scaleRegex, '').trim();
      qrElement.className = cleanClass;

      const canvas = await html2canvas(qrElement, {
        backgroundColor: '#ffffff',
        scale: 2,
      });

      qrElement.className = originalClass;

      const fileName = business?.tag || 'qr-plate';
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `${fileName}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download QR code');
    }
  }, [business]);

  return {
    business,
    loading,
    error,
    activeImageIndex,
    showGalleryModal,
    showMapModal,
    showQRPlateModal,
    socialLinks,
    relatedBusinesses,
    translations,
    selectedLanguage,
    expandedDescription,
    qrPlateData,
    assets,
    banners,
    ratings,
    isOwner,
    images,
    currentImage,
    workingHours,
    aboutSectionRef,
    qrCodeRef,
    navigate,
    setActiveImageIndex,
    setShowGalleryModal,
    setShowMapModal,
    setShowQRPlateModal,
    setSelectedLanguage,
    setExpandedDescription,
    getTranslatedDescription,
    isLongContent,
    getTruncatedContent,
    convertTo12Hour,
    isCurrentlyOpen,
    handleShare,
    handleBack,
    downloadQRCode,
    showImagePreview,
    setShowImagePreview,
    previewImage,
    handleImageClick,
    weather,
    counts,
  };
};
